import {
  SubscriptionFlowDefinition,
  type FlowAnswers,
  type CrossSellConfig,
  type TopUpConfig,
  type FlowFieldRequirements,
  type RecapPolicy,
} from './SubscriptionFlowDefinition'
import type { AvailableBundlesParams } from '@/services/subscription'

/**
 * Health journey (`/depenses-sante`, subcategory COMPL_SANTE).
 *
 * The decision tree consults only doctors/optical/dental; the journey's
 * `extraReimbursements` answer is intentionally not sent (it does not affect the
 * recommendation). Cross-sell add-ons come from each base bundle's upsells, and
 * the Tonic-Études surcomplémentaire (SURCOMPL_SANTE) is offered as a top-up on
 * the Essentielle/Minimale formulas.
 */

const CARE_FREQUENCY_TO_DOCTORS: Record<string, AvailableBundlesParams['doctors']> = {
  often: 'often',
  little: 'rarely',
  no: 'no',
}

const OPTICAL_EXPENSES_TO_OPTICAL: Record<string, AvailableBundlesParams['optical']> = {
  high: 'often',
  low: 'rarely',
  none: 'no',
}

const DENTAL_NEEDS_TO_DENTAL: Record<string, AvailableBundlesParams['dental']> = {
  yesProsthesis: 'yes_prosthesis',
  yes: 'yes',
  no: 'no',
}

export class HealthExpensesFlow extends SubscriptionFlowDefinition {
  readonly product = 'healthExpenses' as const
  readonly subcategory = 'COMPL_SANTE' as const

  /** Source of truth: DecisionTree::ComplSante on the backend. */
  readonly planReference: Record<string, number> = {
    minimal: 1,
    essential: 2,
    confort: 3,
    allRisk: 5,
  }

  readonly fields: FlowFieldRequirements = { ssn: true, banking: true }

  /** Yearly-renewing cover paid upfront or monthly; shows the club block. */
  readonly recap: RecapPolicy = {
    showPaymentSelector: true,
    showClub: true,
    tacitRow: 'yes',
    priceMode: 'duration',
  }

  readonly crossSell: CrossSellConfig = { answerKey: 'crossSell', bundlesExamInsurance: true }

  readonly topUp: TopUpConfig = {
    subcategory: 'SURCOMPL_SANTE',
    reference: 8,
    answerKey: 'topUp',
    // Tonic applies only when the chosen plan offers the top-up.
    isEnabled: (a) => a.plan === 'essential' || a.plan === 'minimal',
  }

  bundlesParams(a: FlowAnswers): AvailableBundlesParams | null {
    if (!a.careFrequency || !a.opticalExpenses || !a.dentalNeeds) return null
    return {
      subcategory: 'COMPL_SANTE',
      doctors: CARE_FREQUENCY_TO_DOCTORS[a.careFrequency],
      optical: OPTICAL_EXPENSES_TO_OPTICAL[a.opticalExpenses],
      dental: DENTAL_NEEDS_TO_DENTAL[a.dentalNeeds],
    }
  }
}
